import type { EraIndex } from '../types';

/**
 * The 6 macro-eras of the founder's journey. Each maps to one or more
 * MGT-413 phases (see `src/data/phases.ts`) and unlocks a floor of the
 * bunker. The matching deterministic stress test lives at
 * `server/stressTests/era<N>.ts`.
 */

export interface EraDefinition {
  index: EraIndex;
  /** Single-word codename rendered in the UI. */
  name: string;
  /** Long-form label used in dropdowns and badges. */
  label: string;
  /** MGT-413 phase indices (0-12) covered by this era. */
  mgtPhases: number[];
  /** One-sentence summary shown in the era unlock cinematic. */
  unlockCriteriaSummary: string;
  /** The central question this era is trying to answer — shown on hover in the Quest Board. */
  centralQuestion: string;
}

export const ERAS: ReadonlyArray<EraDefinition> = [
  {
    index: 0,
    name: 'Identity',
    label: 'Era 0 — Identity',
    mgtPhases: [0],
    unlockCriteriaSummary: 'Founder thesis written + 3 domains listed + skills map',
    centralQuestion: 'Am I the right founder for this problem?',
  },
  {
    index: 1,
    name: 'Atlas',
    label: 'Era 1 — Atlas',
    mgtPhases: [1, 2],
    unlockCriteriaSummary: 'Market Opportunity Navigator complete + 1 beachhead chosen',
    centralQuestion: 'Is there a real opportunity here — and which one?',
  },
  {
    index: 2,
    name: 'Truth',
    label: 'Era 2 — Truth',
    mgtPhases: [3, 4],
    unlockCriteriaSummary: '≥20 interviews filed, ≥60% confirm pain, opportunity memo signed',
    centralQuestion: 'Is the problem I think I see actually real?',
  },
  {
    index: 3,
    name: 'Forge',
    label: 'Era 3 — Forge',
    mgtPhases: [5, 6],
    unlockCriteriaSummary: 'MVP tested with ≥5 prospects + business model canvas validated',
    centralQuestion: 'Does my solution deliver enough value to pay for?',
  },
  {
    index: 4,
    name: 'Trade',
    label: 'Era 4 — Trade',
    mgtPhases: [7, 8, 9],
    unlockCriteriaSummary: '≥3 active pilots + ≥1 paying customer + 6mo runway secured',
    centralQuestion: 'Can I reliably get customers to pay?',
  },
  {
    index: 5,
    name: 'Empire',
    label: 'Era 5 — Empire',
    mgtPhases: [10, 11, 12],
    unlockCriteriaSummary: 'PMF signals confirmed + repeatability proven',
    centralQuestion: 'Can this grow without me?',
  },
];

export function getEra(index: EraIndex): EraDefinition {
  return ERAS[index];
}

/** Returns the era covering a given MGT-413 phase index, or null if invalid. */
export function eraForMgtPhase(mgtPhase: number): EraDefinition | null {
  return ERAS.find((e) => e.mgtPhases.includes(mgtPhase)) ?? null;
}
